import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { getQueryParamsFromUrl, setQueryParamsToUrl } from "../queryParams";
import { useDebounce } from "./useDebounce";

export enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

export type SortType = SortDirection | null;

export interface IGroupParams {
  offset: number;
  limit: number;
  query?: string;
  country?: string | string[];
  sortDirection?: SortType | null;
  timezone?: string | null;
  [key: string]: unknown;
}

interface UseTableParamsOptions {
  initialParams?: Partial<IGroupParams>;
  pageSize?: number;
}

export const useTableParams = (
  { initialParams = {}, pageSize = 25 }: UseTableParamsOptions = {},
  searchValue: string
) => {
  const location = useLocation();

  const [tableParams, setTableParams] = useState<IGroupParams>(() => {
    const urlParams = getQueryParamsFromUrl();
    return {
      ...urlParams,
      offset: Number(urlParams.offset) || 0,
      limit: Number(urlParams.limit) || pageSize,
      query: urlParams.query || "",
      ...initialParams,
    };
  });

  const handleNext = () => {
    setTableParams(prev => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const handlePrev = () => {
    setTableParams(prev => ({
      ...prev,
      offset: Math.max(prev.offset - prev.limit, 0),
    }));
  };

  const updateParams = (newParams: Partial<IGroupParams>) => {
    setTableParams(prev => ({
      ...prev,
      ...newParams,
    }));
  };

  const debouncedSearchValue = useDebounce(searchValue, 500);

  useEffect(() => {
    const urlParams = getQueryParamsFromUrl();

    setTableParams(prev => {
      if (
        prev.offset === Number(urlParams.offset || 0) &&
        prev.limit === Number(urlParams.limit || pageSize) &&
        prev.query === (urlParams.query || "")
      ) {
        return prev;
      }

      return {
        ...prev,
        offset: Number(urlParams.offset) || 0,
        limit: Number(urlParams.limit) || pageSize,
        query: urlParams.query || "",
      };
    });
  }, [location.search, pageSize]);

  useEffect(() => {
    if (debouncedSearchValue !== tableParams.query) {
      setTableParams(prev => ({
        ...prev,
        query: debouncedSearchValue,
        offset: 0,
      }));
    }
  }, [debouncedSearchValue, tableParams.query]);

  useEffect(() => {
    setQueryParamsToUrl(tableParams);
  }, [tableParams]);

  return {
    tableParams,
    setTableParams,
    handleNext,
    handlePrev,
    updateParams,
  };
};
